import React, { useEffect, useState, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "../forms/FormField";
import { TailwindSpinner } from "../Spinner/Spinner";
import MemberEnrollmentTable from "../MemberEnrolements/MemberEnrolmentTable";
import { useDownloadExpiryReport } from "../../hooks/Query/useGetDownloadExpiryReport";
import { ExpiredPointsColumn, UpcomingPointsColumn } from "../Table/TableColums";
import { toast } from "react-toastify";
import { DownloadModal } from "../DownloadModal";
import { downloadFile } from "../../utils/downloadFile";
import { useGetPointExpiryReport } from "../../hooks/Query/useGetPointExpiryReport";

const validationSchema = Yup.object({
  fromDate: Yup.date().required("From date is required"),
  tillDate: Yup.date().required("Till date is required"),
  membershipId: Yup.string(),
  limit: Yup.string(),
  pointType: Yup.string().required("Points type is required"),
  days: Yup.string().when("pointType", {
    is: "1",
    then: (schema) => schema.required("Days is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const RADIO_OPTIONS = [
  { id: "0", name: "Expired" },
  { id: "1", name: "To be expired" },
];
const DOWNLOAD_LIMITS = [
  { value: "100000", label: "100,000" },
  { value: "200000", label: "200,000" },
  { value: "500000", label: "500,000" },
  { value: "1000000", label: "1,000,000" },
  { value: "All", label: "All" },
];

const PointExpiryReport = () => {
  const formikRef = useRef();
  const [formData, setFormData] = useState();
  const [showDownloadOption, setShowDownloadOption] = useState(false);
  const [payloadData, setPayloadData] = useState();
  const [downloadPayloadData, setDownloadPayloadData] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [totalPage, setTotalPage] = useState(0);
  const [shouldDownload, setShouldDownload] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("");
  const [expiryReportData, setExpiryReportData] = useState([]);
  const [page, setPage] = useState(1);

  // Download report hook
  const { data: downloadExpiryReport, isLoading: loading } =
    useDownloadExpiryReport({ payload: downloadPayloadData });

  // Point expiry report hook
  const {
    data: pointExpiryReport,
    isFetching: loadingReport,
    refetch,
    status,
  } = useGetPointExpiryReport({ payload: payloadData, page });

  // Build payload string for download modal ("All" limit case)
  const payload = `from_date=${formData?.fromDate}&to_date=${formData?.tillDate}&membership_id=${formData?.membershipId}&point_status=${formData?.pointType}&days=${formData?.days}&limit=${formData?.limit}`;

  // Sync form values to state
  useEffect(() => {
    const interval = setInterval(() => {
      if (formikRef.current?.values) setFormData(formikRef.current.values);
    }, 300);
    return () => clearInterval(interval);
  }, []);

  // Show download modal if limit is "All"
  useEffect(() => {
    if (formData?.limit === "All") {
      setOpenModal(true);
      setShowDownloadOption(false);
    }
  }, [formData]);

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    refetch();
  };

  const handleSubmit = (values) => {
    setPayloadData(values);
    setPage(1);
  };

  const handleShowDownloadReport = (values) => {
    if (!values.fromDate || !values.tillDate) {
      toast.error("Kindly fill all required fields.");
      return;
    }
    setShowDownloadOption((prev) => !prev);
  };

  const handleDownloadReport = (values, format) => {
    if (!values) {
      toast.error("An error occurred");
      return;
    }
    setDownloadPayloadData(values);
    setDownloadFormat(format);
    setShouldDownload(true);
  };

  useEffect(() => {
    if (shouldDownload && downloadExpiryReport?.data?.length > 0) {
      downloadFile({
        data: downloadExpiryReport?.data,
        format: downloadFormat,
        fileName: `FirstRewards Point Expiry Report ${downloadPayloadData?.fromDate} - ${downloadPayloadData?.tillDate}`,
      });
      setShouldDownload(false);
    } else if (
      shouldDownload &&
      !loading &&
      !downloadExpiryReport?.data?.length
    ) {
      toast.warning("No data available to download.");
      setShouldDownload(false);
    }
  }, [downloadExpiryReport, shouldDownload, downloadFormat, loading]);

  useEffect(() => {
    if (!loadingReport && pointExpiryReport) {
      setExpiryReportData(pointExpiryReport?.data || []);
      setTotalPage(pointExpiryReport?.pagination?.total_pages || 1);
    } else if (status === "error") {
      toast.error("An error occurred");
      setExpiryReportData([]);
    }
  }, [pointExpiryReport, loadingReport, status]);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 bg-white rounded-lg">
      <div className="mb-6">
        <h1 className="text-lg font-medium text-gray-900">Points Expiry</h1>
        <p className="text-grey-200 text-sm">View all point expiry report in the program</p>
      </div>

      <Formik
        innerRef={formikRef}
        initialValues={{
          fromDate: "",
          tillDate: "",
          membershipId: "",
          limit: "",
          pointType: "",
          days: "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => handleSubmit(values)}
      >
        {({ values }) => (
          <Form className="md:w-[711px] relative h-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <FormField label="From Date" name="fromDate" type="date" required />
              <FormField label="Till Date" name="tillDate" type="date" required />
              <FormField label="Membership ID" name="membershipId" type="text" />
              <FormField label="Points type" name="pointType" type="radio" required radioOptions={RADIO_OPTIONS} />
              {values?.pointType === "1" && (
                <FormField
                  label="Days"
                  name="days"
                  type="select"
                  options={[
                    { value: 30, label: 30 },
                    { value: 60, label: 60 },
                    { value: 90, label: 90 },
                  ]}
                  required
                />
              )}
              {showDownloadOption && (
                <FormField label="Download Limit" name="limit" type="select" options={DOWNLOAD_LIMITS} />
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-4 mt-6 justify-between items-center">
              <button
                type="submit"
                disabled={loadingReport}
                className="bg-mysecondary hover:bg-blue-800 text-white px-8 py-2 rounded flex items-center justify-center w-[189px] h-[38px] text-sm"
              >
                {loadingReport ? <TailwindSpinner /> : "Search"}
              </button>
              <div>
                <button
                  type="button"
                  className="text-center border-1 border-mysecondary text-blue-900 hover:bg-blue-50 px-8 py-2 rounded flex items-center justify-center w-[189px] h-[38px] text-sm"
                  onClick={() => handleShowDownloadReport(values)}
                >
                  {" Download As"}
                </button>
              </div>
            </div>
            {showDownloadOption && (
              <div className="flex justify-center md:justify-end">
                <div className="right-0 mt-2 text-center border-1 border-mysecondary text-blue-900 hover:bg-blue-50 rounded flex items-center justify-end w-[189px] flex-col">
                  <button
                    type="button"
                    className="border-b border-blue-900 w-full text-sm py-2 hover:bg-mysecondary hover:text-white"
                    onClick={() => handleDownloadReport(values, "pdf")}
                  >
                    Pdf
                  </button>
                  <button
                    type="button"
                    className="border-b border-blue-900 w-full text-sm py-2 hover:bg-mysecondary hover:text-white"
                    onClick={() => handleDownloadReport(values, "csv")}
                  >
                    Csv
                  </button>
                </div>
              </div>
            )}
          </Form>
        )}
      </Formik>

      {expiryReportData?.length > 0 ? (
        <MemberEnrollmentTable
          columns={formData?.pointType === "1" ? UpcomingPointsColumn : ExpiredPointsColumn}
          data={expiryReportData}
          page={page}
          setPage={setPage}
          handlePageChange={handlePageChange}
          totalPage={totalPage}
          loading={loadingReport}
          title="Point Expiry"
        />
      ) : (
        <div className="text-sm text-center text-gray-500 mt-10">
          No data found
        </div>
      )}
      {openModal && (
        <DownloadModal
          setOpenModal={setOpenModal}
          value={formData}
          openModal={openModal}
          payload={payload}
        />
      )}
    </div>
  );
};

export default PointExpiryReport;